import React from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import BaseWidget from 'nucleus-widgets/lib/BaseWidget';
import renderRichText from 'nucleus-widgets/utils/renderRichText';
import Speakers from './Speakers.js';
import SpeakersStyles from './SpeakersStyles.less';
import { resolveTestId, injectTestId } from '@cvent/nucleus-test-automation';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { Speaker as SpeakerType, SpeakerCategory as SpeakerCategoryType } from '../../interfaces/eventPropTypes';
import SpeakersWidgetBehavior, { defaultBehavior } from './SpeakersWidgetBehavior';

const SpeakersConfig = PropTypes.shape({
  headerText: PropTypes.string.isRequired,
  instructionalText: PropTypes.any.isRequired,
  displayOptions: PropTypes.shape({
    prefix: PropTypes.bool.isRequired,
    title: PropTypes.bool.isRequired,
    company: PropTypes.bool.isRequired
  }),
  viewBioModal: PropTypes.shape({
    displayModal: PropTypes.bool.isRequired,
    modalButtonText: PropTypes.string.isRequired,
    modalDisplayOptions: PropTypes.arrayOf(PropTypes.string)
  }),
  filter: PropTypes.shape({
    categoryGrouping: PropTypes.bool.isRequired
  })
});

export default class SpeakersWidget extends BaseWidget {
  static defaultProps = {
    classes: {
      ...SpeakersStyles,
      keywordFilter: {
        textbox: SpeakersStyles.filterTextbox,
        label: {
          hidden: SpeakersStyles.hidden
        }
      }
    }
  }

  static propTypes = {
    config: SpeakersConfig.isRequired,
    style: PropTypes.any.isRequired,
    classes: PropTypes.any.isRequired,
    translate: PropTypes.func.isRequired,
    speakers: PropTypes.arrayOf(SpeakerType),
    speakerCategories: PropTypes.arrayOf(SpeakerCategoryType)
  }

  getStyleObject() {
    return {
      ...super.getStyleObject(),
      speakerHeader: this.getElementInlineStyle('speakerHeader'),
      speakerInstructionalText: this.getElementInlineStyle('speakerInstructionalText'),
      speakerName: this.getElementInlineStyle('speakerName'),
      speakerTitle: this.getElementInlineStyle('speakerTitle'),
      speakerCompany: this.getElementInlineStyle('speakerCompany'),
      speakerCard: this.getElementInlineStyle('speakerCard'),
      speakerProfileImage: this.getElementInlineStyle('speakerProfileImage'),
      viewBioButton: {
        button: this.getElementInlineStyle('viewBioButton')
      },
      viewBioButtonContainer: { textAlign: this.getElementInlineStyle('viewBioButton').textAlign },
      speakerModalName: this.getElementInlineStyle('speakerModalName'),
      speakerModalDesignation: this.getElementInlineStyle('speakerModalDesignation'),
      speakerModalTitle: this.getElementInlineStyle('speakerModalTitle'),
      speakerModalExit: this.getElementInlineStyle('speakerModalExit'),
      speakerModalCompany: this.getElementInlineStyle('speakerModalCompany'),
      speakerModalBiography: this.getElementInlineStyle('speakerModalBiography'),
      speakerContactIcons: this.getElementInlineStyle('speakerContactIcons'),
      categoryGrouping: this.getElementInlineStyle('categoryGrouping'),
      speakerModalWebsites: this.getElementInlineStyle('speakerModalWebsites'),
      speakerModalWebsitesTitle: this.getElementInlineStyle('speakerModalWebsitesTitle'),
      speakerModalDocuments: this.getElementInlineStyle('speakerModalDocuments'),
      speakerModalDocumentsTitle: this.getElementInlineStyle('speakerModalDocumentsTitle'),
      keywordFilter: {
        textbox: this.getElementInlineStyle('keywordFilter')
      }
    };
  }
  render() {
    const { config: { headerText, instructionalText, displayOptions, viewBioModal, filter },
      translate, classes, speakers, speakerCategories, style, dialogContainerStyle,
      loadSpeakerDocuments, getSpeakerDocuments } = this.props;
    const sortedSpeakers = speakers.sort((a, b) => { return a.order - b.order; });
    const styleProps = { ...this.props, style: this.getStyleObject(), classes };
    const testId = injectTestId('speakers');
    return (
      <div {...resolve(this.props, 'widgetContainer')}>
        <div {...resolve(styleProps, 'container')} {...resolveTestId(testId)} >
          <div {...resolve(styleProps, 'speakerHeader')} {...resolveTestId(testId, '-header')}>
            { translate(headerText) }
          </div>
          <div
            {...resolve(styleProps, 'speakerInstructionalText')}
            dangerouslySetInnerHTML={{ __html: renderRichText(instructionalText, translate, style.palette,
              styleProps.style.speakerInstructionalText) }}
            {...resolveTestId(testId, '-instructionalText')}
          />
          <Speakers
            {...styleProps}
            {...testId}
            displayOptions={displayOptions}
            speakers={sortedSpeakers}
            speakerCategories={speakerCategories}
            viewBioModal={viewBioModal}
            filter={filter}
            dialogContainerStyle={dialogContainerStyle}
            loadSpeakerDocuments={loadSpeakerDocuments}
            getSpeakerDocuments={getSpeakerDocuments}
          />
        </div>
      </div>
    );
  }
}

export function widgetWithBehavior(behavior) {
  const invalidKeys = Object.keys(behavior).filter(k => !defaultBehavior[k]);
  if (invalidKeys.length > 0) {
    throw new Error('invalid keys in SpeakersWidgetBehavior: ' + invalidKeys.join(','));
  }

  return props => {
    const dispatch = useDispatch();
    const mergedBehavior = {
      ...defaultBehavior,
      ...behavior,
      recordViewSpeakerProfileActivity: bindActionCreators(behavior.recordViewSpeakerProfileActivity, dispatch),
      recordSpeakerFileDownloadActivity: bindActionCreators(behavior.recordSpeakerFileDownloadActivity, dispatch)
    };

    return (
      <SpeakersWidgetBehavior.Provider value={mergedBehavior}>
        <SpeakersWidget {...props} />
      </SpeakersWidgetBehavior.Provider>
    );
  };
}

